import React, { useEffect, useState } from 'react';
import { Mail, Plus, Save, Trash2 } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import {
  getEmailNotificationSettings,
  updateEmailNotificationSettings,
} from '../../staffPlatformService';
import { EmailNotificationSettings } from '../../types/staffPlatform';

const EmailNotificationPage: React.FC = () => {
  const { user, staffProfile } = useAuth();
  const [settings, setSettings] = useState<EmailNotificationSettings>({
    notificationEmails: [],
    notifyOnVendorSubmission: true,
    notifyOnVendorUpdate: true,
    notifyOnVendorApproval: true,
  });
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await getEmailNotificationSettings();
      if (result.success && result.settings) {
        setSettings(result.settings);
      }
      setLoading(false);
    };
    load();
  }, []);

  const addEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;
    if (settings.notificationEmails.includes(trimmed)) {
      setMessage({ type: 'error', text: 'Email already in the list.' });
      return;
    }
    setSettings((prev) => ({
      ...prev,
      notificationEmails: [...prev.notificationEmails, trimmed],
    }));
    setNewEmail('');
  };

  const removeEmail = (email: string) => {
    setSettings((prev) => ({
      ...prev,
      notificationEmails: prev.notificationEmails.filter((e) => e !== email),
    }));
  };

  const handleSave = async () => {
    if (settings.notificationEmails.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one notification email.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    const updatedByEmail = user?.username || staffProfile?.email || '';
    const updatedByName = user?.name || staffProfile?.name || 'Staff';

    const result = await updateEmailNotificationSettings(settings, updatedByEmail, updatedByName);
    if (result.success) {
      setMessage({ type: 'success', text: 'Email notification settings saved.' });
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to save settings.' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
          Configure who receives alerts for vendor activity.
        </p>

      {message && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            message.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="h-5 w-5 text-sunbeth-orange-600" />
            Notification Recipients
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            These addresses receive vendor activity alerts.
          </p>

          <div className="mt-4 flex gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
              placeholder="email@example.com"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunbeth-orange-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={addEmail}
              className="px-4 py-2 bg-sunbeth-orange-600 text-white rounded-lg hover:bg-sunbeth-orange-700 flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>

          <ul className="mt-4 space-y-2">
            {settings.notificationEmails.map((email) => (
              <li
                key={email}
                className="flex items-center justify-between px-4 py-2 bg-gray-50 rounded-lg border border-gray-200"
              >
                <span className="text-sm text-gray-900">{email}</span>
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  className="text-red-600 hover:text-red-800 p-1"
                  aria-label={`Remove ${email}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
            {settings.notificationEmails.length === 0 && (
              <li className="text-sm text-gray-500 py-2">No notification emails configured.</li>
            )}
          </ul>
        </div>

        <div className="border-t border-gray-200 pt-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Alert Triggers</h2>

          {[
            {
              key: 'notifyOnVendorSubmission' as const,
              label: 'Notify on vendor submission',
              description: 'Send email when a new vendor application is submitted.',
            },
            {
              key: 'notifyOnVendorUpdate' as const,
              label: 'Notify on vendor update',
              description: 'Send email when a vendor updates their profile or documents.',
            },
            {
              key: 'notifyOnVendorApproval' as const,
              label: 'Notify on vendor approval',
              description: 'Send email when a vendor is approved or rejected.',
            },
          ].map((toggle) => (
            <label
              key={toggle.key}
              className="flex items-start gap-3 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={settings[toggle.key]}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, [toggle.key]: e.target.checked }))
                }
                className="mt-1 rounded border-gray-300 text-sunbeth-orange-600 focus:ring-sunbeth-orange-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900 group-hover:text-sunbeth-orange-700">
                  {toggle.label}
                </span>
                <p className="text-xs text-gray-500">{toggle.description}</p>
              </div>
            </label>
          ))}
        </div>

        <div className="border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-sunbeth-orange-600 text-white rounded-lg hover:bg-sunbeth-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailNotificationPage;
